// Portfolio Administration v1.0 PR 3 — TanStack Query hooks for the
// Loan-detail surface. Mirrors the @/hooks/dealFlow conventions:
//   • Stable query-key factory.
//   • Mutation hooks invalidate the touched queries on success.
//   • Toasts + ApiError rendering happen in the calling component.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  loanAssignmentsApi,
  loanCalendarApi,
  loanInsuranceApi,
  loanPartiesApi,
  loanProfileFieldsApi,
  loanSetupApi,
  loanSetupCatalogApi,
  loanUccApi,
  loansApi,
  portfoliosApi,
  requiredVendorsApi,
  vendorConnectionsApi,
  vendorsApi,
} from "@/api/portfolioAdmin/client";
import { covenantCalibrationApi } from "@/api/portfolioAdmin/covenantCalibrationApi";
import { workQueueKeys } from "@/hooks/compliance/useWorkQueues";
import {
  invalidateLoanCalendar,
  invalidateLoanHealth,
  invalidateLoanSummaryAndHealth,
} from "./invalidation";
import type {
  LoanIntakeBundleDto,
  UpdateLoanIntakeProfileRequest,
} from "@/types/intakeProfile";
import type {
  AddLoanPartyRequest,
  AssignLoanRoleRequest,
  CompleteCalendarItemRequest,
  CovenantCalibrationDto,
  UpdateCalibrationAmountsRequest,
  UpdateSeasonalityRequest,
  UpsertBankAccountRequest,
  CompleteSetupItemRequest,
  ComplianceCalendarItemDto,
  CreateLoanProfileFieldDefinitionRequest,
  CreateSetupCatalogItemRequest,
  CreateVendorRequest,
  FileUccAmendmentRequest,
  FileUccContinuationRequest,
  FileUccTerminationRequest,
  InsurancePolicyDto,
  JustificationRequest,
  LoanAssignmentDto,
  LoanListItemDto,
  LoanPartyAssignmentDto,
  LoanProfileFieldDefinitionDto,
  LoanProfileFieldValueDto,
  LoanSearchParams,
  LoanSetupCatalogItemDto,
  LoanSetupChecklistRowDto,
  LoanSummaryDto,
  MoveLoansToPortfolioRequest,
  CreatePortfolioRequest,
  PortfolioDashboardDto,
  PortfolioListItemDto,
  UpdatePortfolioRequest,
  RecordUccLienSearchRequest,
  RequiredVendorCatalogItemDto,
  SnoozeCalendarItemRequest,
  UccFilingDto,
  UccLienSearchDto,
  UpdateLoanPartyRequest,
  UpdateLoanProfileFieldDefinitionRequest,
  UpdateLoanTermsRequest,
  UpdateSetupCatalogItemRequest,
  UpsertInsurancePolicyRequest,
  UpsertLoanFinancialPeriodRequest,
  VendorDto,
} from "@/api/portfolioAdmin/types";

// ============================== query keys ============================

export const portfolioAdminKeys = {
  all: ["portfolio-admin"] as const,
  loanDetail: (loanId: string) =>
    ["portfolio-admin", "loan", loanId] as const,
  intakeBundle: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "intake-bundle"] as const,
  setup: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "setup"] as const,
  assignments: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "assignments"] as const,
  parties: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "parties"] as const,
  vendorConnections: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "vendor-connections"] as const,
  profileFields: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "profile-fields"] as const,
  covenantCalibration: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "covenant-calibration"] as const,
  setupCatalog: () => ["portfolio-admin", "setup-catalog"] as const,
  profileFieldDefinitions: () =>
    ["portfolio-admin", "profile-field-definitions"] as const,
  insurance: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "insurance"] as const,
  uccFilings: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "ucc", "filings"] as const,
  uccSearches: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "ucc", "searches"] as const,
  calendar: (loanId: string, sourceType?: string, status?: string) =>
    [
      "portfolio-admin",
      "loan",
      loanId,
      "calendar",
      sourceType ?? null,
      status ?? null,
    ] as const,
  requiredVendorCatalog: () =>
    ["portfolio-admin", "required-vendor-catalog"] as const,
  vendors: (params?: { name?: string; type?: string; status?: string }) =>
    [
      "portfolio-admin",
      "vendors",
      params?.name ?? null,
      params?.type ?? null,
      params?.status ?? null,
    ] as const,
  loanList: (params?: LoanSearchParams) =>
    [
      "portfolio-admin",
      "loans",
      "list",
      params?.search ?? null,
      (params?.portfolioIds ?? []).join(",") || null,
      (params?.accountStates ?? []).join(",") || null,
      params?.assignedB1UserId ?? null,
      params?.assignedToMe ?? false,
    ] as const,
  portfolios: () => ["portfolio-admin", "portfolios"] as const,
  portfolioDashboard: (id: string) =>
    ["portfolio-admin", "portfolios", id, "dashboard"] as const,
};

// ============================== loan summary ==========================

export function useLoan(loanId: string | undefined) {
  return useQuery<LoanSummaryDto>({
    queryKey: portfolioAdminKeys.loanDetail(loanId ?? ""),
    queryFn: () => loansApi.get(loanId!),
    enabled: !!loanId,
  });
}

export function useLoansList(params?: LoanSearchParams, enabled = true) {
  return useQuery<LoanListItemDto[]>({
    queryKey: portfolioAdminKeys.loanList(params),
    queryFn: () => loansApi.search(params),
    enabled,
  });
}

// ----- Portfolios -------------------------------------------------------
// Named loan groupings. Mutations invalidate the registry, the loans list
// (membership changed), the batch grades, and the touched loans' detail +
// health caches (the LS-3 setup row may have auto-evaluated).

export function usePortfolios(enabled = true) {
  return useQuery<PortfolioListItemDto[]>({
    queryKey: portfolioAdminKeys.portfolios(),
    queryFn: () => portfoliosApi.list(),
    enabled,
  });
}

export function usePortfolioDashboard(id: string | undefined) {
  return useQuery<PortfolioDashboardDto>({
    queryKey: portfolioAdminKeys.portfolioDashboard(id ?? ""),
    queryFn: () => portfoliosApi.dashboard(id!),
    enabled: !!id,
  });
}

export function useCreatePortfolio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePortfolioRequest) => portfoliosApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portfolioAdminKeys.portfolios() });
    },
  });
}

export function useUpdatePortfolio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePortfolioRequest }) =>
      portfoliosApi.update(id, body),
    onSuccess: (_dto, { id }) => {
      void qc.invalidateQueries({ queryKey: portfolioAdminKeys.portfolios() });
      void qc.invalidateQueries({
        queryKey: portfolioAdminKeys.portfolioDashboard(id),
      });
    },
  });
}

export function useMoveLoansToPortfolio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MoveLoansToPortfolioRequest) =>
      loansApi.moveToPortfolio(body),
    onSuccess: (_result, body) => {
      void qc.invalidateQueries({ queryKey: ["portfolio-admin", "loans"] });
      void qc.invalidateQueries({ queryKey: portfolioAdminKeys.portfolios() });
      if (body.portfolioId) {
        void qc.invalidateQueries({
          queryKey: portfolioAdminKeys.portfolioDashboard(body.portfolioId),
        });
      }
      for (const loanId of body.loanIds) {
        void qc.invalidateQueries({
          queryKey: portfolioAdminKeys.loanDetail(loanId),
        });
      }
    },
  });
}

// Deal Submission spec §6.1 — intake profile + financial
// history carried forward from the closed Deal.
export function useLoanIntakeBundle(loanId: string | undefined) {
  return useQuery<LoanIntakeBundleDto>({
    queryKey: portfolioAdminKeys.intakeBundle(loanId ?? ""),
    queryFn: () => loansApi.intakeBundle(loanId!),
    enabled: !!loanId,
  });
}

// ----- Profile tab editing (B6) ---------------------------------------
// Terms invalidate the loan summary (caption + header figures); carried
// fields + financial history invalidate the intake bundle.

export function useUpdateLoanTerms(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateLoanTermsRequest) =>
      loansApi.updateTerms(loanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.loanDetail(loanId) });
    },
  });
}

export function useUpdateLoanIntakeProfile(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateLoanIntakeProfileRequest) =>
      loansApi.updateIntakeProfile(loanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.intakeBundle(loanId) });
    },
  });
}

export function useAddLoanFinancialPeriod(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertLoanFinancialPeriodRequest) =>
      loansApi.addFinancialPeriod(loanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.intakeBundle(loanId) });
    },
  });
}

export function useUpdateLoanFinancialPeriod(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { rowId: string; body: UpsertLoanFinancialPeriodRequest }) =>
      loansApi.updateFinancialPeriod(loanId, args.rowId, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.intakeBundle(loanId) });
    },
  });
}

// ====================== covenant calibration ==========================
// Capture-only Profile-tab cards (calibration amounts / seasonality /
// DSCR-DACA banking). Every mutation refetches BOTH the calibration record
// (the cards re-render with the refreshed confirm caption) AND the loan
// summary (its covenantCalibrationConfirmed flips the Profile tab badge).

/** Refetch the calibration record + the loan summary — used after every
 *  covenant capture mutation so the cards AND the tab badge stay fresh. */
function invalidateCovenantCalibration(
  qc: ReturnType<typeof useQueryClient>,
  loanId: string,
) {
  qc.invalidateQueries({
    queryKey: portfolioAdminKeys.covenantCalibration(loanId),
  });
  qc.invalidateQueries({ queryKey: portfolioAdminKeys.loanDetail(loanId) });
}

export function useCovenantCalibration(loanId: string | undefined) {
  return useQuery<CovenantCalibrationDto>({
    queryKey: portfolioAdminKeys.covenantCalibration(loanId ?? ""),
    queryFn: () => covenantCalibrationApi.get(loanId!),
    enabled: !!loanId,
  });
}

export function useUpdateCalibrationAmounts(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCalibrationAmountsRequest) =>
      covenantCalibrationApi.updateAmounts(loanId, body),
    onSuccess: () => invalidateCovenantCalibration(qc, loanId),
  });
}

export function useConfirmCalibration(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => covenantCalibrationApi.confirmCalibration(loanId),
    onSuccess: () => invalidateCovenantCalibration(qc, loanId),
  });
}

export function useUpdateSeasonality(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSeasonalityRequest) =>
      covenantCalibrationApi.updateSeasonality(loanId, body),
    onSuccess: () => invalidateCovenantCalibration(qc, loanId),
  });
}

export function useConfirmSeasonality(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => covenantCalibrationApi.confirmSeasonality(loanId),
    onSuccess: () => invalidateCovenantCalibration(qc, loanId),
  });
}

export function useAddCovenantBankAccount(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertBankAccountRequest) =>
      covenantCalibrationApi.addAccount(loanId, body),
    onSuccess: () => invalidateCovenantCalibration(qc, loanId),
  });
}

export function useUpdateCovenantBankAccount(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { accountId: string; body: UpsertBankAccountRequest }) =>
      covenantCalibrationApi.updateAccount(loanId, args.accountId, args.body),
    onSuccess: () => invalidateCovenantCalibration(qc, loanId),
  });
}

export function useRemoveCovenantBankAccount(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) =>
      covenantCalibrationApi.removeAccount(loanId, accountId),
    onSuccess: () => invalidateCovenantCalibration(qc, loanId),
  });
}

export function useConfirmBanking(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => covenantCalibrationApi.confirmBanking(loanId),
    onSuccess: () => invalidateCovenantCalibration(qc, loanId),
  });
}

// ============================== setup checklist =======================

export function useLoanSetupChecklist(loanId: string | undefined) {
  return useQuery<LoanSetupChecklistRowDto[]>({
    queryKey: portfolioAdminKeys.setup(loanId ?? ""),
    queryFn: () => loanSetupApi.list(loanId!),
    enabled: !!loanId,
  });
}

export function useCompleteSetupItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { catalogItemId: string; body: CompleteSetupItemRequest }) =>
      loanSetupApi.complete(loanId, args.catalogItemId, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setup(loanId) });
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.loanDetail(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useCertifySetupItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (catalogItemId: string) =>
      loanSetupApi.certify(loanId, catalogItemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setup(loanId) });
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.loanDetail(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useDeclineSetupItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { catalogItemId: string; body: JustificationRequest }) =>
      loanSetupApi.decline(loanId, args.catalogItemId, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setup(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useWaiveSetupItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { catalogItemId: string; body: JustificationRequest }) =>
      loanSetupApi.waive(loanId, args.catalogItemId, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setup(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useMarkSetupComplete(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => loanSetupApi.markComplete(loanId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.loanDetail(loanId) });
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setup(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useGenerateSetupChecklist(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => loanSetupApi.generate(loanId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setup(loanId) });
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.loanDetail(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

// ====================== setup catalog admin ===========================

export function useLoanSetupCatalog(enabled = true) {
  return useQuery<LoanSetupCatalogItemDto[]>({
    queryKey: portfolioAdminKeys.setupCatalog(),
    queryFn: () => loanSetupCatalogApi.list(),
    enabled,
  });
}

/** loanId: the loan whose checklist should refresh so a new catalog item's
 *  lazy-created Pending row appears immediately. */
export function useCreateSetupCatalogItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSetupCatalogItemRequest) =>
      loanSetupCatalogApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setupCatalog() });
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setup(loanId) });
      // The lazy-created Pending row can flip this loan's setup light.
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useUpdateSetupCatalogItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: UpdateSetupCatalogItemRequest }) =>
      loanSetupCatalogApi.update(args.id, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setupCatalog() });
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.setup(loanId) });
      // Tier edits propagate to every loan's gate; refresh at least the
      // loan in view (no portfolio-wide health key exists).
      invalidateLoanHealth(qc, loanId);
    },
  });
}

// ============================== assignments ===========================

export function useLoanAssignments(loanId: string | undefined) {
  return useQuery<LoanAssignmentDto[]>({
    queryKey: portfolioAdminKeys.assignments(loanId ?? ""),
    queryFn: () => loanAssignmentsApi.list(loanId!),
    enabled: !!loanId,
  });
}

export function useUpsertLoanAssignment(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { roleCode: string; body: AssignLoanRoleRequest }) =>
      loanAssignmentsApi.upsert(loanId, args.roleCode, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.assignments(loanId) });
    },
  });
}

// ============================== parties ===============================

export function useLoanParties(loanId: string | undefined, includeInactive = false) {
  return useQuery<LoanPartyAssignmentDto[]>({
    // Extends the parties(...) prefix so the mutation invalidations below
    // hit both the active-only and include-inactive variants.
    queryKey: [...portfolioAdminKeys.parties(loanId ?? ""), includeInactive],
    queryFn: () => loanPartiesApi.list(loanId!, includeInactive),
    enabled: !!loanId,
  });
}

export function useSeedPartiesFromDeal(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => loanPartiesApi.seedFromDeal(loanId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.parties(loanId) });
      // …and the summary (borrower display) + health.
      void invalidateLoanSummaryAndHealth(qc, loanId);
    },
  });
}

export function useAddLoanParty(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddLoanPartyRequest) => loanPartiesApi.add(loanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.parties(loanId) });
      // …and the summary (borrower display) + health.
      void invalidateLoanSummaryAndHealth(qc, loanId);
    },
  });
}

export function useUpdateLoanParty(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { partyAssignmentId: string; body: UpdateLoanPartyRequest }) =>
      loanPartiesApi.update(args.partyAssignmentId, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.parties(loanId) });
      // …and the summary (borrower display) + health.
      void invalidateLoanSummaryAndHealth(qc, loanId);
    },
  });
}

export function useInactivateLoanParty(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partyAssignmentId: string) =>
      loanPartiesApi.inactivate(partyAssignmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.parties(loanId) });
      // Ending the assignment auto-disconnects any live connection.
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.vendorConnections(loanId) });
      // …and the summary (borrower display) + health.
      void invalidateLoanSummaryAndHealth(qc, loanId);
    },
  });
}

// ================= Vendor Connection v1 ("Make a Connection") ==========

export function useLoanVendorConnections(loanId: string | undefined) {
  return useQuery({
    queryKey: portfolioAdminKeys.vendorConnections(loanId ?? ""),
    queryFn: () => vendorConnectionsApi.listForLoan(loanId!),
    enabled: !!loanId,
  });
}

export function useConnectVendor(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partyAssignmentId: string) =>
      vendorConnectionsApi.connect(partyAssignmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.vendorConnections(loanId) });
    },
  });
}

export function useDisconnectVendor(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { connectionId: string; reason?: string }) =>
      vendorConnectionsApi.disconnect(vars.connectionId, vars.reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.vendorConnections(loanId) });
    },
  });
}

// ====================== loan profile admin fields =====================

export function useLoanProfileFieldDefinitions(includeInactive = false) {
  return useQuery<LoanProfileFieldDefinitionDto[]>({
    queryKey: [...portfolioAdminKeys.profileFieldDefinitions(), includeInactive],
    queryFn: () => loanProfileFieldsApi.listDefinitions(includeInactive),
  });
}

export function useLoanProfileFieldValues(loanId: string | undefined) {
  return useQuery<LoanProfileFieldValueDto[]>({
    queryKey: portfolioAdminKeys.profileFields(loanId ?? ""),
    queryFn: () => loanProfileFieldsApi.values(loanId!),
    enabled: !!loanId,
  });
}

export function useCreateProfileFieldDefinition(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLoanProfileFieldDefinitionRequest) =>
      loanProfileFieldsApi.createDefinition(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.profileFieldDefinitions() });
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.profileFields(loanId) });
    },
  });
}

export function useUpdateProfileFieldDefinition(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: UpdateLoanProfileFieldDefinitionRequest }) =>
      loanProfileFieldsApi.updateDefinition(args.id, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.profileFieldDefinitions() });
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.profileFields(loanId) });
    },
  });
}

export function useSetLoanProfileFieldValue(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { definitionId: string; value: string | null }) =>
      loanProfileFieldsApi.setValue(loanId, args.definitionId, { value: args.value }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.profileFields(loanId) });
    },
  });
}

// ============================== insurance (§7.4) ======================

export function useLoanInsurance(loanId: string | undefined) {
  return useQuery<InsurancePolicyDto[]>({
    queryKey: portfolioAdminKeys.insurance(loanId ?? ""),
    queryFn: () => loanInsuranceApi.list(loanId!),
    enabled: !!loanId,
  });
}

export function useCreateInsurancePolicy(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertInsurancePolicyRequest) =>
      loanInsuranceApi.create(loanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.insurance(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useUpdateInsurancePolicy(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { policyId: string; body: UpsertInsurancePolicyRequest }) =>
      loanInsuranceApi.update(args.policyId, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.insurance(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useVerifyInsurancePolicy(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { policyId: string; party: "phoenix" | "seller" }) =>
      args.party === "phoenix"
        ? loanInsuranceApi.verifyPhoenix(args.policyId)
        : loanInsuranceApi.verifySeller(args.policyId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.insurance(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

// ============================== UCC (§7.5) ============================

export function useLoanUccFilings(loanId: string | undefined) {
  return useQuery<UccFilingDto[]>({
    queryKey: portfolioAdminKeys.uccFilings(loanId ?? ""),
    queryFn: () => loanUccApi.listFilings(loanId!),
    enabled: !!loanId,
  });
}

export function useFileUccContinuation(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FileUccContinuationRequest) =>
      loanUccApi.fileContinuation(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.uccFilings(loanId) });
      // Filings reshape the lapse calendar items + the UCC health light.
      invalidateLoanCalendar(qc, loanId);
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useFileUccTermination(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FileUccTerminationRequest) =>
      loanUccApi.fileTermination(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.uccFilings(loanId) });
      // Filings reshape the lapse calendar items + the UCC health light.
      invalidateLoanCalendar(qc, loanId);
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useFileUccAmendment(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FileUccAmendmentRequest) =>
      loanUccApi.fileAmendment(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.uccFilings(loanId) });
      // Filings reshape the lapse calendar items + the UCC health light.
      invalidateLoanCalendar(qc, loanId);
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useUccLienSearches(loanId: string | undefined) {
  return useQuery<UccLienSearchDto[]>({
    queryKey: portfolioAdminKeys.uccSearches(loanId ?? ""),
    queryFn: () => loanUccApi.listSearches(loanId!),
    enabled: !!loanId,
  });
}

export function useRecordUccLienSearch(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordUccLienSearchRequest) =>
      loanUccApi.recordSearch(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.uccSearches(loanId) });
      // Recording a search advances its calendar cadence + the UCC light.
      invalidateLoanCalendar(qc, loanId);
      invalidateLoanHealth(qc, loanId);
    },
  });
}

// ============================== compliance calendar (§7.6) ============

export function useLoanDueDates(
  loanId: string | undefined,
  params?: { sourceType?: string; status?: string },
) {
  return useQuery<ComplianceCalendarItemDto[]>({
    queryKey: portfolioAdminKeys.calendar(
      loanId ?? "",
      params?.sourceType,
      params?.status,
    ),
    queryFn: () => loanCalendarApi.list(loanId!, params),
    enabled: !!loanId,
  });
}

export function useCompleteCalendarItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { itemId: string; body: CompleteCalendarItemRequest }) =>
      loanCalendarApi.complete(args.itemId, args.body),
    onSuccess: () => {
      invalidateLoanCalendar(qc, loanId);
      invalidateLoanHealth(qc, loanId);
      // Calendar ticklers also feed the compliance cockpit work queue.
      qc.invalidateQueries({ queryKey: workQueueKeys.compliance });
    },
  });
}

export function useSnoozeCalendarItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { itemId: string; body: SnoozeCalendarItemRequest }) =>
      loanCalendarApi.snooze(args.itemId, args.body),
    onSuccess: () => {
      invalidateLoanCalendar(qc, loanId);
      invalidateLoanHealth(qc, loanId);
      // Calendar ticklers also feed the compliance cockpit work queue.
      qc.invalidateQueries({ queryKey: workQueueKeys.compliance });
    },
  });
}

// ============== required-vendor catalog (Assign-vendor role dropdown) ===

/** Loan-independent vendor-type catalog that seeds the "Assign vendor" Role
 *  dropdown. (The per-loan vacancy matrix + its covenant automation were
 *  retired.) */
export function useRequiredVendorCatalog() {
  return useQuery<RequiredVendorCatalogItemDto[]>({
    queryKey: portfolioAdminKeys.requiredVendorCatalog(),
    queryFn: () => requiredVendorsApi.catalog(),
  });
}

// ============================== vendors (registry) ====================

export function useVendors(params?: {
  name?: string;
  type?: string;
  status?: string;
}) {
  return useQuery<VendorDto[]>({
    queryKey: portfolioAdminKeys.vendors(params),
    queryFn: () => vendorsApi.search(params),
  });
}

export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVendorRequest) => vendorsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio-admin", "vendors"] });
    },
  });
}
